/**
 * AI俳優「入れ替え」の位置置換 導出純関数 (FE 側)。
 *
 * docs/features/scenario-actor-replace-plan.md §3.1.1 の導出規則を TS に移植したもの。
 * バックエンドの正本 (`backend/services/scenario/utils/actor_replacement.py`
 * `derive_positional_replacements`) と同じ入出力規則を持つ純関数で、FE では
 * 保存前プレビュー (§3.4) にのみ使う — 実際のシーン書き換えは必ずサーバー側の
 * 再導出で行われるため、この関数の結果を payload に含めて送ることはしない
 * (§3.1 レビュー1巡 P1-2: クライアント申告の置換マッピングは配列と本文の不整合を
 * 作れるため禁止)。
 *
 * `before` / `after` は同一ファミリー側の配列 (casting_model_ids 同士、または
 * seedance_preset_actor_ids 同士) を渡すこと。ファミリーが変わった場合の扱いは
 * 呼び出し側で判定する (§3.5: ファミリー跨ぎは対象外)。
 */

export interface PositionalReplacement {
  position: number;
  fromId: string;
  toId: string;
}

/**
 * `before` → `after` が「同じ位置の1対1置換の集合」として説明できるときだけ
 * 置換集合を返す。次の5条件をすべて満たさない場合は null (導出失敗)。
 *
 * 1. `before.length === after.length` (人数変更は置換ではない)
 * 2. `after` に重複 ID がない (`before` にもない。既存データに重複があれば導出失敗)
 * 3. 位置ごとの差分集合 `changed` が空でない (何も変わっていなければ導出失敗)
 * 4. `changed` の各位置で、`after[i]` が `before` に含まれず、`before[i]` が
 *    `after` に含まれない (並べ替え・スワップを置換と誤認しない)
 * 5. `changed` 以外の位置はすべて `before[i] === after[i]` (3の定義から自明)
 */
export function derivePositionalReplacements(
  before: string[],
  after: string[],
): PositionalReplacement[] | null {
  // 条件1: 人数変更は置換ではない
  if (before.length !== after.length) return null;

  // 条件2: after (および before) に重複 ID があれば導出しない
  const afterSet = new Set(after);
  if (afterSet.size !== after.length) return null;
  const beforeSet = new Set(before);
  if (beforeSet.size !== before.length) return null;

  // 条件3: 差分位置の集合
  const changed: number[] = [];
  for (let i = 0; i < before.length; i += 1) {
    if (before[i] !== after[i]) changed.push(i);
  }
  if (changed.length === 0) return null;

  // 条件4: 並べ替え・スワップの排除
  for (const i of changed) {
    if (beforeSet.has(after[i])) return null;
    if (afterSet.has(before[i])) return null;
  }

  // 条件5 は changed の定義から自明 (changed 以外は before[i] === after[i])
  return changed.map((i) => ({ position: i, fromId: before[i], toId: after[i] }));
}
